import { useMutation, useQuery, useQueryClient } from "react-query"
import { deleteProperty, getDeletedProperties, getDeletedProperty, getProperties, getProperty, restoreProperty, saveProperty } from "./propertyApi"
import { notification } from "antd"
import { createNewProperty } from "./propertyModel"

export const usePropertyListQuery = (customerId: number | undefined, showDeletedView: boolean) => {
  return useQuery(['properties', {customerId}], () => showDeletedView ? getDeletedProperties() : getProperties(customerId!), {
    enabled: !!customerId || showDeletedView,
    refetchOnWindowFocus: false
  })
}

export const usePropertyInvalidator = () => {
  const queryClient = useQueryClient()
  return {
    invalidatePropertyList: (customerId: number | undefined) => {
      queryClient.invalidateQueries(['properties', {customerId}])
    }
  }
}

export const usePropertyQuery = (customerId: number | undefined, propertyId: number | undefined, showDeletedView: boolean) => {
  return useQuery(['property', {customerId, propertyId}], () => {
    if (showDeletedView) {
      return getDeletedProperty(propertyId!)
    }
    if (propertyId === 0) {
      return createNewProperty(customerId!)
    }
    return getProperty(customerId!, propertyId!)
  }, {
    enabled: (!!customerId || showDeletedView) && propertyId !== undefined,
    refetchOnWindowFocus: false
  })
}

export const useSavePropertyMutation = (onSuccess?: (propertyId: number) => void) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: saveProperty,
    onSuccess: (result, {customerId, property}) => {
      queryClient.invalidateQueries(['properties', {customerId}])
      queryClient.invalidateQueries(['property', {customerId, propertyId: property.id}])
      onSuccess?.(result.id)
    },
    onError: () => {
      notification.error({
        message: 'There was a problem saving the property.'
      })
    }
  })
}

export const useDeletePropertyMutation = (onSuccess?: () => void) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: deleteProperty,
    onSuccess: (_, { customerId, propertyId }) => {
      queryClient.invalidateQueries(['properties', {customerId}])
      queryClient.invalidateQueries(['property', {customerId, propertyId}])
      onSuccess?.()
    },
    onError: () => {
      notification.error({
        message: 'There was a problem removing this property.'
      })
    }
  })
}

export const useRestorePropertyMutation = (onSuccess?: () => void) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: restoreProperty,
    onSuccess: (_, propertyId) => {
      queryClient.invalidateQueries(['properties', {customerId: undefined}])
      queryClient.invalidateQueries(['property', {customerId: undefined, propertyId}])
      notification.success({
        message: 'The property has been successfully restored.'
      })
      onSuccess?.()
    },
    onError: () => {
      notification.error({
        message: 'There was a problem restoring this property.'
      })
    }
  })
}
